import { Box, IconButton } from '@wix/design-system';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { useRef, useState, useEffect } from 'react';


import css from './index.module.scss';

type RelatedQuestionsSliderProps = {
  relatedQuestions: string[];
  onQuestionClick?: (question: string, index: number) => void;
};

export const RelatedQuestionsSlider = ({
  relatedQuestions,
  onQuestionClick,
}: RelatedQuestionsSliderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = containerRef.current;
    if (!el) { return; }
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = containerRef.current;
    if (!el) { return; }
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [relatedQuestions]);

  const scrollToEdge = (direction: 'left' | 'right') => {
    const el = containerRef.current;
    if (!el) { return; }
    el.scrollTo({
      left: direction === 'right' ? el.scrollWidth - el.clientWidth : 0,
      behavior: 'smooth',
    });
  };

  return (
    <Box className={css.wrapper}>
      <div
        className={css.sliderContainer}
        ref={containerRef}
      >
        {relatedQuestions.map((question, index) => (
          <button
            key={question}
            className={css.tag}
            onClick={() => onQuestionClick?.(question, index)}
          >
            {question}
          </button>
        ))}
      </div>
      {canScrollLeft && (
        <IconButton
          className={`${css.arrowButton} ${css.arrowButtonLeft}`}
          onClick={() => scrollToEdge('left')}
          size="tiny"
          skin="dark"
          priority="secondary"
        >
          <ChevronLeft />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          className={`${css.arrowButton} ${css.arrowButtonRight}`}
          onClick={() => scrollToEdge('right')}
          size="tiny"
          skin="dark"
          priority="secondary"
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
};
